import { DesktopDropdownContent, DesktopDropdownItem } from '@crowdex/ui/components/desktop';
import {
  DropdownMenu,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@crowdex/ui/components/dropdown-menu';
import { ChevronDown, LoaderCircle, MessageCircle, Plus } from 'lucide-react';
import { type KeyboardShortcut, shortcutLabel } from '@/lib/platform';

/** The shortcut that reaches the floating composer from the fullscreen view (D58). */
export const FLOATING_COMPOSER_SHORTCUT: KeyboardShortcut = { key: 'l', commandOrControl: true };

/** A conversation a floating composer may write to instead (its chip's menu). */
export type ComposerTargetChoice = { id: string; title: string };

/**
 * Who a floating composer writes to (D60): "to <conversation>", with the way back to the whole
 * conversation and, where the composer may write elsewhere (an artifact's page), the other
 * conversations to choose from and a new one.
 */
export function FloatingTargetChip({
  title,
  onOpenConversation,
  choices,
  onChoose,
  offerNew = true,
}: {
  title: string;
  onOpenConversation?: (() => void) | undefined;
  /** The conversations it may write to instead; with them, "New conversation" (`null`). */
  choices?: readonly ComposerTargetChoice[] | undefined;
  onChoose?: ((conversation: string | null) => void) | undefined;
  /** Whether "New conversation" is offered (not when it already writes to a new one). */
  offerNew?: boolean;
}) {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          aria-label={`To ${title}`}
          className="type-meta flex h-8 min-w-0 max-w-64 shrink items-center gap-1.5 rounded-control px-2.5 text-muted-foreground transition-colors hover:bg-control hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          type="button"
        >
          <MessageCircle className="size-3.5 shrink-0" />
          <span className="truncate">to {title}</span>
          <ChevronDown className="size-3 shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DesktopDropdownContent align="start" className="min-w-52" side="top" sideOffset={8}>
        {onOpenConversation !== undefined && (
          <DesktopDropdownItem onSelect={onOpenConversation}>Open conversation</DesktopDropdownItem>
        )}
        {onChoose !== undefined && (
          <>
            {onOpenConversation !== undefined && (choices?.length || offerNew) ? (
              <DropdownMenuSeparator />
            ) : null}
            {(choices ?? []).map((choice) => (
              <DesktopDropdownItem key={choice.id} onSelect={() => onChoose(choice.id)}>
                <span className="truncate">{choice.title}</span>
              </DesktopDropdownItem>
            ))}
            {offerNew && (
              <DesktopDropdownItem onSelect={() => onChoose(null)}>
                <Plus className="size-3.5" />
                New conversation
              </DesktopDropdownItem>
            )}
          </>
        )}
      </DesktopDropdownContent>
    </DropdownMenu>
  );
}

/**
 * The folded floating composer's pill (D58): "Ask about this… ⌘L", and what runs. Drawn over the
 * empty editor only while the composer is folded (see `globals.css`).
 */
export function FloatingPillHint({ running }: { running: boolean }) {
  return (
    <span aria-hidden className="desktop-floating-pill-hint">
      <span className="truncate">Ask about this…</span>
      <kbd className="type-caption shrink-0 rounded border border-hairline-strong px-1 font-sans text-muted-foreground">
        {shortcutLabel(FLOATING_COMPOSER_SHORTCUT)}
      </kbd>
      {running && (
        <span className="flex shrink-0 items-center gap-1.5 border-hairline border-l pl-2.5">
          <LoaderCircle className="size-3.5 animate-spin" />
          Working
        </span>
      )}
    </span>
  );
}
